import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Avatar, Chip, IconButton, InputAdornment, TextField, Tooltip, useTheme, useMediaQuery, Card, CardContent, Grid, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, List, ListItemButton, ListItemAvatar, ListItemText, Divider,
  Stack, alpha,
} from '@mui/material';
import {
  Search as SearchIcon, WhatsApp as WhatsAppIcon, Phone as PhoneIcon,
  Chat as ChatIcon, People as PeopleIcon, FilterList as FilterListIcon,
  Circle as CircleIcon, Edit as EditIcon, Delete as DeleteIcon, Person as PersonIcon, Groups as GroupsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { atendimentoService, AtendimentoContato, AtendimentoConexao } from '../../services/atendimentoService';
import { contactService } from '../../services/contactService';
import tagService from '../../services/tagService';
import { Tag } from '../../types';
import { getCountryByNumber } from '../../utils/countries';
import ContactForm from '../../components/forms/ContactForm';
import SetoresAllSelect from './componentes/SetoresAllSelect';
import ContatoAtendimentoDetalhesDialog from './componentes/ContatoAtendimentoDetalhesDialog';
import { usePermissions } from '../../contexts/AuthContext';

const CORES_AVATAR = ['#1565c0', '#6a1b9a', '#2e7d32', '#c62828', '#e65100', '#0277bd', '#4a148c', '#1b5e20', '#880e4f', '#33691e'];

const getInitials = (name: string) => name ? name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase() : '?';

interface KpiCardProps {
  titulo: string;
  valor: string | number;
  subtitulo?: string;
  icone: React.ReactNode;
  cor: string;
  bgCor: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ titulo, valor, subtitulo, icone, cor, bgCor }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      borderRadius: 2,
      border: (t) => `1px solid ${alpha(t.palette.divider, 0.8)}`,
    }}
  >
    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
        <Box minWidth={0}>
          <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
            {titulo}
          </Typography>
          <Typography variant="h6" fontWeight={800} color={cor} lineHeight={1.2}>
            {valor}
          </Typography>
          {subtitulo && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {subtitulo}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: bgCor, width: 34, height: 34, '& .MuiSvgIcon-root': { fontSize: 18 } }}>{icone}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

const obterDadosPais = (numero: string, ddi?: string | null) => {
  if (ddi) return getCountryByNumber(ddi);
  let limpo = numero.replace(/\D/g, '');
  if (limpo.startsWith('55') && (limpo.length === 12 || limpo.length === 13)) {
    return getCountryByNumber('55');
  }
  if (limpo.length === 10 || limpo.length === 11) {
    return getCountryByNumber('55');
  }
  return getCountryByNumber(limpo);
};

const formatarNumeroComDddParenteses = (numero: string, ddi?: string | null): string => {
  if (!numero) return '';
  let limpo = numero.replace(/\D/g, '');

  let ddiEncontrado = ddi || '';
  if (!ddiEncontrado && limpo.startsWith('55') && (limpo.length === 12 || limpo.length === 13)) {
    ddiEncontrado = '55';
  }

  if (ddiEncontrado && limpo.startsWith(ddiEncontrado)) {
    limpo = limpo.slice(ddiEncontrado.length);
  }

  if (limpo.length === 10) {
    return `${ddiEncontrado ? '+' + ddiEncontrado + ' ' : ''}(${limpo.slice(0, 2)}) ${limpo.slice(2, 6)}-${limpo.slice(6)}`;
  } else if (limpo.length === 11) {
    return `${ddiEncontrado ? '+' + ddiEncontrado + ' ' : ''}(${limpo.slice(0, 2)}) ${limpo.slice(2, 7)}-${limpo.slice(7)}`;
  }

  return numero;
};

const ContatosAtendimentoPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [busca, setBusca] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [contatos, setContatos] = useState<AtendimentoContato[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    tagService.getAll({ limit: 1000 }).then((res) => {
      if (res.success && res.data) {
        setAllTags(res.data);
      }
    }).catch(() => { });
  }, []);

  const getContactTagChips = useCallback((tagsRaw: any) => {
    if (!tagsRaw) return [];
    let rawArray: any[] = [];
    if (Array.isArray(tagsRaw)) {
      rawArray = tagsRaw;
    } else if (typeof tagsRaw === 'string') {
      try {
        const parsed = JSON.parse(tagsRaw);
        if (Array.isArray(parsed)) rawArray = parsed;
      } catch (e) {
        rawArray = [];
      }
    }
    const ids = rawArray.map((v) => typeof v === 'object' && v !== null ? Number(v.id) : Number(v)).filter((n) => !isNaN(n));
    return allTags.filter((t) => ids.includes(t.id));
  }, [allTags]);
  const [modalAberto, setModalAberto] = useState(false);
  const [contatoSelecionado, setContatoSelecionado] = useState<AtendimentoContato | null>(null);
  const [contatoDetalhes, setContatoDetalhes] = useState<AtendimentoContato | null>(null);
  const [conexoes, setConexoes] = useState<AtendimentoConexao[]>([]);
  const [setorId, setSetorId] = useState<number | null>(null);
  const [loadingConexoes, setLoadingConexoes] = useState(false);
  const [iniciandoId, setIniciandoId] = useState<number | null>(null);
  const [erroModal, setErroModal] = useState('');
  const navigate = useNavigate();
  const { can } = usePermissions();
  const podeCriarNovoTicket = can('atendimento.criar_novo_ticket');

  // Estados para edição do contato
  const [modalEditarAberto, setModalEditarAberto] = useState(false);
  const [contatoParaEditar, setContatoParaEditar] = useState<any>(null);
  const [salvandoContato, setSalvandoContato] = useState(false);

  // Estados para exclusão
  const [modalDeletarAberto, setModalDeletarAberto] = useState(false);
  const [contatoParaDeletar, setContatoParaDeletar] = useState<AtendimentoContato | null>(null);
  const [deletandoContato, setDeletandoContato] = useState(false);
  const [modalAvisoClienteAberto, setModalAvisoClienteAberto] = useState(false);

  const abrirModalDeletar = (contato: AtendimentoContato) => {
    if (contato.cliente_id) {
      setModalAvisoClienteAberto(true);
    } else {
      setContatoParaDeletar(contato);
      setModalDeletarAberto(true);
    }
  };

  const handleDeletarContato = async () => {
    if (!contatoParaDeletar) return;
    setDeletandoContato(true);
    try {
      await atendimentoService.deleteContato(contatoParaDeletar.id);
      setModalDeletarAberto(false);
      fetchContatos();
    } catch (error: any) {
      console.error('Erro ao deletar contato:', error);
      alert(error?.response?.data?.error || 'Erro ao deletar contato');
    } finally {
      setDeletandoContato(false);
    }
  };

  const abrirModalEditar = (contato: AtendimentoContato) => {
    const mapped: any = {
      ...contato,
      tipo: contato.numero ? 'celular' : 'email',
      is_whatsapp: contato.canal === 'whatsapp',
      recebe_notificacao: true,
      is_default: false,
      bot_desativado: contato.bot_desativado || false,
      tags: contato.tags || [],
    };
    setContatoParaEditar(mapped);
    setModalEditarAberto(true);
  };

  const abrirModalNovo = () => {
    setContatoParaEditar(null);
    setModalEditarAberto(true);
  };

  const handleSalvarContato = async (data: any) => {
    setSalvandoContato(true);
    try {
      if (contatoParaEditar) {
        const payload: any = {
          nome: data.nome,
          tipo: data.tipo,
          numero: data.numero,
          ddi: data.ddi,
          is_whatsapp: data.is_whatsapp,
          cliente_id: data.cliente_id,
          is_default: data.is_default,
          recebe_notificacao: data.recebe_notificacao,
          email: data.email,
          bot_desativado: data.bot_desativado,
          tags: data.tags,
        };
        await atendimentoService.updateContato(contatoParaEditar.id, payload);
      } else {
        const createPayload = {
          nome: data.nome,
          tipo: data.tipo,
          numero: data.numero,
          email: data.email,
          is_whatsapp: data.is_whatsapp,
          cliente_id: data.cliente_id,
          recebe_notificacao: data.recebe_notificacao,
          ddi: data.ddi,
          bot_desativado: data.bot_desativado,
          tags: data.tags,
        };
        await contactService.create(createPayload as any);
      }

      setModalEditarAberto(false);
      fetchContatos();
    } catch (error: any) {
      console.error('Erro ao salvar contato:', error);
      alert(error?.response?.data?.error || error?.response?.data?.message || 'Erro ao salvar contato');
    } finally {
      setSalvandoContato(false);
    }
  };

  const fetchContatos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await atendimentoService.getContatos(busca);
      setContatos(data);
    } catch (error) {
      console.error('Erro ao carregar contatos de atendimento:', error);
    } finally {
      setLoading(false);
    }
  }, [busca]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchContatos();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [fetchContatos]);

  const totalWhatsapp = contatos.filter((c) => c.canal === 'whatsapp').length;
  const totalClientes = contatos.filter((c) => c.cliente_id).length;

  const paginatedContatos = contatos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const conexoesDisponiveis = conexoes.filter((c) => c.ativo && c.tipo === 'whatsapp');

  const abrirModalIniciar = async (contato: AtendimentoContato) => {
    setContatoSelecionado(contato);
    setModalAberto(true);
    setSetorId(null);
    setErroModal('');
    setLoadingConexoes(true);
    try {
      const data = await atendimentoService.getConexoes();
      setConexoes(data);
    } catch (error: any) {
      setErroModal(error?.response?.data?.error || error?.message || 'Erro ao carregar conexoes.');
    } finally {
      setLoadingConexoes(false);
    }
  };

  const iniciarAtendimento = async (conexao: AtendimentoConexao) => {
    if (!contatoSelecionado) return;
    setIniciandoId(conexao.id);
    setErroModal('');
    try {
      const { ticket } = await atendimentoService.iniciarTicket(contatoSelecionado.id, conexao.id, setorId);
      setModalAberto(false);
      navigate('/atendimento/atendimentos', { state: { ticketId: ticket.id } });
    } catch (error: any) {
      setErroModal(error?.response?.data?.error || error?.message || 'Erro ao iniciar atendimento.');
    } finally {
      setIniciandoId(null);
    }
  };

  const abrirDetalhesContato = (contato: AtendimentoContato) => {
    setContatoDetalhes(contato);
  };

  const headerGradient =
    theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${alpha(theme.palette.info.dark, 0.35)} 0%, ${alpha(theme.palette.primary.main, 0.25)} 50%, ${alpha('#1a1a2e', 0.9)} 100%)`
      : `linear-gradient(135deg, ${alpha('#e3f2fd', 0.95)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 45%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`;

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 }, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          p: { xs: 1.25, md: 1.5 },
          borderRadius: 2,
          background: headerGradient,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <PeopleIcon sx={{ fontSize: 120, color: theme.palette.primary.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
              }}
            >
              <PeopleIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
                Contatos do Atendimento
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Contatos sincronizados no sistema de multi-canal
              </Typography>
            </Box>
          </Stack>
          <Button variant="contained" color="primary" onClick={abrirModalNovo} disableElevation size="small">
            Novo Contato
          </Button>
        </Stack>
      </Paper>

      {!loading && contatos.length > 0 && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={4}>
            <KpiCard
              titulo="Total Localizado"
              valor={contatos.length}
              subtitulo="na pesquisa atual"
              icone={<GroupsIcon sx={{ color: theme.palette.primary.main }} />}
              cor={theme.palette.primary.main}
              bgCor={alpha(theme.palette.primary.main, 0.12)}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <KpiCard
              titulo="WhatsApp"
              valor={totalWhatsapp}
              subtitulo="contatos do whatsapp"
              icone={<WhatsAppIcon sx={{ color: '#25d366' }} />}
              cor="#25d366"
              bgCor={alpha('#25d366', 0.12)}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <KpiCard
              titulo="Clientes Vinculados"
              valor={totalClientes}
              subtitulo={`${contatos.length ? Math.round((totalClientes / contatos.length) * 100) : 0}% do total`}
              icone={<PersonIcon sx={{ color: theme.palette.info.main }} />}
              cor={theme.palette.info.main}
              bgCor={alpha(theme.palette.info.main, 0.12)}
            />
          </Grid>
        </Grid>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.25, md: 1.5 },
          mb: 1.5,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.04 : 0.02),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} mb={1}>
          <FilterListIcon color="primary" sx={{ fontSize: 18 }} />
          <Typography variant="body2" fontWeight={700}>
            Filtros
          </Typography>
        </Stack>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar por nome ou número..."
          value={busca}
          onChange={(e) => { setBusca(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" fontWeight={700}>
            Listagem de Contatos
          </Typography>
          {!loading && (
            <Chip
              size="small"
              label={`${contatos.length} contato${contatos.length !== 1 ? 's' : ''}`}
              color="primary"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5, gap: 1 }}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">
              Carregando contatos...
            </Typography>
          </Box>
        ) : paginatedContatos.length === 0 ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <PeopleIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Nenhum contato encontrado
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Ajuste a busca ou cadastre um novo contato.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      '& .MuiTableCell-head': {
                        fontWeight: 700,
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        color: 'text.secondary',
                        py: 0.75,
                        px: 1,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      },
                    }}
                  >
                    <TableCell>Contato</TableCell>
                    {!isMobile && <TableCell>Número</TableCell>}
                    <TableCell>Cliente</TableCell>
                    {!isMobile && <TableCell>Tags / Canal</TableCell>}
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedContatos.map((contato) => {
                    const avatarCor = CORES_AVATAR[contato.id % CORES_AVATAR.length];
                    const nomeDisplay = contato.nome || contato.cliente?.nome || 'Desconhecido';

                    return (
                      <TableRow
                        key={contato.id}
                        hover
                        onClick={() => abrirDetalhesContato(contato)}
                        sx={{ cursor: 'pointer', '& .MuiTableCell-root': { py: 0.6, px: 1 } }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar src={atendimentoService.getAvatarUrl(contato.foto_perfil)} sx={{ bgcolor: avatarCor, width: 32, height: 32, fontSize: '0.7rem' }}>
                              {!contato.foto_perfil && getInitials(nomeDisplay)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600} lineHeight={1.2}>{nomeDisplay}</Typography>
                              {isMobile && <Typography variant="caption" color="text.secondary">{formatarNumeroComDddParenteses(contato.numero, contato.ddi)}</Typography>}
                            </Box>
                          </Box>
                        </TableCell>

                        {!isMobile && (
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {contato.canal === 'whatsapp' ? <WhatsAppIcon sx={{ fontSize: 14, color: '#25d366' }} /> : <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
                              {(() => {
                                const country = obterDadosPais(contato.numero, contato.ddi);
                                return country ? (
                                  <Tooltip title={country.label}>
                                    <img
                                      loading="lazy"
                                      width="16"
                                      src={new URL(`../../assets/countries/w20/${country.code.toLowerCase()}.png`, import.meta.url).href}
                                      alt={country.label}
                                      style={{ borderRadius: 2 }}
                                    />
                                  </Tooltip>
                                ) : null;
                              })()}
                              <Typography variant="body2">{formatarNumeroComDddParenteses(contato.numero, contato.ddi)}</Typography>
                            </Box>
                          </TableCell>
                        )}

                        <TableCell>
                          {contato.cliente?.nome ? (
                            <Chip
                              label={contato.cliente.nome}
                              size="small"
                              color="primary"
                              variant="outlined"
                              clickable
                              onClick={(event) => {
                                event.stopPropagation();
                                navigate(`/clients/${contato.cliente?.id}`);
                              }}
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.disabled">Não vinculado</Typography>
                          )}
                        </TableCell>

                        {!isMobile && (
                          <TableCell>
                            <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
                              {getContactTagChips(contato.tags).map((t) => (
                                <Chip
                                  key={t.id}
                                  label={t.titulo}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    bgcolor: alpha(t.color, 0.15),
                                    color: t.color,
                                    border: `1px solid ${alpha(t.color, 0.3)}`,
                                  }}
                                />
                              ))}
                              <Chip label={contato.canal} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18, color: contato.canal === 'whatsapp' ? '#25d366' : 'inherit', borderColor: contato.canal === 'whatsapp' ? '#25d366' : 'inherit' }} />
                              {contato.is_grupo && <Chip label="grupo" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />}
                              {contato.bot_desativado && <Chip label="bot desativado" size="small" variant="outlined" color="error" sx={{ fontSize: '0.65rem', height: 18 }} />}

                            </Box>
                          </TableCell>
                        )}

                        <TableCell align="center">
                          <Box display="flex" justifyContent="center" gap={0.5}>
                            <Tooltip title="Editar contato">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  abrirModalEditar(contato);
                                }}
                              >
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir contato">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  abrirModalDeletar(contato);
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            {podeCriarNovoTicket && (
                              <Tooltip title="Iniciar atendimento">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    abrirModalIniciar(contato);
                                  }}
                                >
                                  <ChatIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <TablePagination
              component="div"
              size="small"
              count={contatos.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              labelRowsPerPage="Linhas:"
              sx={{
                minHeight: 44,
                '.MuiTablePagination-toolbar': { minHeight: 44, px: 1.5 },
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem', my: 0 },
              }}
            />
          </>
        )}
      </Paper>

      <Dialog open={modalAberto} onClose={() => !iniciandoId && setModalAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <ChatIcon color="success" />
            <Box>
              <Typography variant="h6" fontWeight={700}>Iniciar atendimento</Typography>
              {contatoSelecionado && (
                <Typography variant="body2" color="text.secondary">
                  {contatoSelecionado.nome || contatoSelecionado.numero}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {erroModal && <Alert severity="error" sx={{ m: 2 }}>{erroModal}</Alert>}

          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <SetoresAllSelect
              value={setorId}
              onChange={setSetorId}
              disabled={!!iniciandoId}
              label="Setor"
              placeholder="Selecione o setor"
            />
          </Box>

          {loadingConexoes ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress size={28} />
            </Box>
          ) : conexoesDisponiveis.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Nenhuma conexao WhatsApp ativa encontrada.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {conexoesDisponiveis.map((cx, index) => {
                const conectada = cx.status === 'conectado';
                const statusColor = conectada ? '#2e7d32' : cx.status === 'aguardando_qr' ? '#e65100' : cx.status === 'conectando' ? '#0277bd' : '#c62828';

                return (
                  <React.Fragment key={cx.id}>
                    {index > 0 && <Divider />}
                    <ListItemButton disabled={!!iniciandoId || !conectada} onClick={() => iniciarAtendimento(cx)} sx={{ py: 1.3 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: conectada ? '#e8f5e9' : 'action.hover' }}>
                          <WhatsAppIcon sx={{ color: conectada ? '#25d366' : 'text.disabled' }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight={700}>{cx.nome}</Typography>
                            <Chip size="small" label={cx.status} icon={<CircleIcon sx={{ fontSize: '8px !important', color: `${statusColor} !important` }} />} sx={{ height: 20, fontSize: '0.65rem' }} />
                          </Box>
                        }
                        secondary={cx.numero ? `Numero: ${cx.numero}` : 'Sem numero vinculado'}
                      />
                      {iniciandoId === cx.id && <CircularProgress size={20} />}
                    </ListItemButton>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalAberto(false)} disabled={!!iniciandoId}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      <ContactForm
        open={modalEditarAberto}
        onClose={() => setModalEditarAberto(false)}
        onSubmit={handleSalvarContato}
        editingContact={contatoParaEditar}
        clientId={contatoParaEditar?.cliente_id || undefined}
        loading={salvandoContato}
      />

      <ContatoAtendimentoDetalhesDialog
        open={Boolean(contatoDetalhes)}
        contato={contatoDetalhes}
        onClose={() => setContatoDetalhes(null)}
      />

      <Dialog open={modalDeletarAberto} onClose={() => !deletandoContato && setModalDeletarAberto(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o contato <strong>{contatoParaDeletar?.nome || contatoParaDeletar?.numero}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita. O histórico de conversas será mantido.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalDeletarAberto(false)} disabled={deletandoContato}>Cancelar</Button>
          <Button onClick={handleDeletarContato} color="error" variant="contained" disabled={deletandoContato} disableElevation>
            {deletandoContato ? <CircularProgress size={24} color="inherit" /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modalAvisoClienteAberto} onClose={() => setModalAvisoClienteAberto(false)}>
        <DialogTitle>Aviso</DialogTitle>
        <DialogContent>
          <Typography>
            Este contato está vinculado a um cliente. Só é possível excluí-lo no cadastro do cliente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalAvisoClienteAberto(false)} variant="contained" disableElevation>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContatosAtendimentoPage;
